// Note: always use "credentials: 'same-origin'" even if this is the default value because iOS >= 10 <= 12 does not follow the standard !
import * as React from 'react'
import { flash$ } from "./flash.service"
import { I18nService } from "./i18n.service"
import { removeDiacritics } from "./utils.service"
import i18nScopedData from './http.service.yml'

const i18nScoped = new I18nService(i18nScopedData)
const headers = Object.freeze({ 'Accept': 'application/json', 'Content-Type': 'application/json' })
let assetsManifest: { [path: string]: string } = null
httpLoadManifest()

export function httpLoadManifest () {
  if (assetsManifest) return

  if (typeof window !== 'undefined' && window.cdn_url) {
    const inCache = sessionStorage.getItem(`${__DEPLOYED_AT__}-rev-manifest.json`)

    if (inCache) {
      assetsManifest = JSON.parse(inCache)
    } else {
      const request = new XMLHttpRequest()
      request.open('GET', `${window.cdn_url}/rev-manifest.json`, false)  // `false` makes the request synchronous
      request.send(null)
      assetsManifest = JSON.parse(request.responseText)
      sessionStorage.setItem(`${__DEPLOYED_AT__}-rev-manifest.json`, JSON.stringify(assetsManifest))
    }
  }
}

export function httpGetAssetPath (path: string) {
  if  (!window.cdn_url) return `/assets/${path}`
  return `${ window.cdn_url}/assets/${assetsManifest[path]}`
}

export function httpGetRoute () {
  if (window.currentUser?.type === 'Customer') return 'account'
  if (window.currentUser?.type === 'Employee') return 'account_employee'
  if (window.currentUser?.market_type === 'Supplier') return 'backoffice'
  if (window.currentUser?.market_type === 'Marketplace') return 'market' 
  if (window.currentUser?.market_type === 'Prescriber') return 'prescription' 
  if (window.market_type === 'Supplier') return `suppliers/${window.market_id}`
  if (window.market_type === 'Marketplace') return `marketplaces/${window.market_id}`
  if (window.market_type === 'Prescriber') return `prescribers/${window.market_id}`
  throw new Error('Unable to resolve route')
}

export function httpGetPublicRoute () {
  return window.market_type.toLowerCase()
}

export async function httpGet (url: string, params: any = {}) {
  return httpProcessResponse(await fetch(url + httpGetQueryParams(params), { method: 'GET', headers, credentials: 'same-origin' }))
}

export async function httpPost (url: string, body: any) {
  return httpProcessResponse(await fetch(url, { method: 'POST', body: JSON.stringify(body), headers: { ...headers, 'X-CSRF-Token': httpGetCSRFToken() }, credentials: 'same-origin' }))
}

export async function httpPostSync (url: string, body: any) {
  const request = new XMLHttpRequest()
  request.open('POST', url, false)  // `false` makes the request synchronous
  request.setRequestHeader('Accept', 'application/json')
  request.setRequestHeader('Content-Type', 'application/json')
  request.send(JSON.stringify(body))
  return JSON.parse(request.responseText)
}

export async function httpPostMultipart (url: string, body: FormData) {
  return httpProcessResponse(await fetch(url, { method: 'POST', body, headers: { 'Accept': 'application/json', 'X-CSRF-Token': httpGetCSRFToken() }, credentials: 'same-origin' }))
}

export async function httpPatch (url: string, body: any) {
  return httpProcessResponse(await fetch(url, { method: 'PATCH', body: JSON.stringify(body), headers: { ...headers, 'X-CSRF-Token': httpGetCSRFToken() }, credentials: 'same-origin' }))
}

export async function httpPatchMultipart (url: string, body: FormData) {
  return httpProcessResponse(await fetch(url, { method: 'PATCH', body, headers: { 'Accept': 'application/json', 'X-CSRF-Token': httpGetCSRFToken() }, credentials: 'same-origin' }))
}

export async function httpPut (url: string, body: any) {
  return httpProcessResponse(await fetch(url, { method: 'PUT', body: JSON.stringify(body), headers: { ...headers, 'X-CSRF-Token': httpGetCSRFToken() }, credentials: 'same-origin' }))
}

export async function httpDelete (url: string, params: any = {}) {
  return httpProcessResponse(await fetch(url + httpGetQueryParams(params), { method: 'DELETE', headers: { ...headers, 'X-CSRF-Token': httpGetCSRFToken() }, credentials: 'same-origin' }))
}

export type RecoverableError = {
  full_messages: string[]
  ui_handler?: {
    component?: string | null
    confirm_message?: string | null
    skipable_with?: Record<string, unknown> | null
    [key: string]: unknown
  } | null
}

type UiHandlerComponent = React.ComponentType<{ onResult: (result: boolean) => void, onCancel: () => void, recoverableError: RecoverableError, [key: string]: unknown }>
export type UiHandlerComponentsMap = Record<string, UiHandlerComponent>

export async function httpRecoverable (method: 'GET' | 'POST' | 'PATCH' | 'PUT', url: string, body: any, hostPropertyForSkipable: string, uiHandlerComponents?: UiHandlerComponentsMap) {
  let response, json: any
  let bodyCopy = JSON.parse(JSON.stringify(body))

  while (true) {
    response = method === 'GET'
      ? await fetch(url + httpGetQueryParams(bodyCopy), { method: 'GET', headers, credentials: 'same-origin' })
      : await fetch(url, { method, body: JSON.stringify(bodyCopy), headers: { ...headers, 'X-CSRF-Token': httpGetCSRFToken() }, credentials: 'same-origin' })

    json = await httpParseJsonAndFlash(response)

    const componentName = json.ui_handler?.component
    const matchedComponent = componentName && uiHandlerComponents?.[componentName]
    const hasSkipableWith = !!json.ui_handler?.skipable_with

    // not hable to recover if neither the default and the customer handler can apply
    if (!hasSkipableWith && !matchedComponent) break

    // dynamic import: modals.service is a .tsx file, static import would break mocha/node type stripping
    const { modalOpen, modalOpenConfirm } = await import("./modals.service")

    let confirmed: boolean
    if (matchedComponent) {
      confirmed = await modalOpen<boolean>(controls => React.createElement(uiHandlerComponents[componentName], { ...controls, recoverableError: json }))
    } else {
      confirmed = await modalOpenConfirm(i18nScoped.t('confirm_continue'), json.ui_handler?.confirm_message)
    }

    if (!confirmed || !hasSkipableWith) break
    if (hostPropertyForSkipable) bodyCopy[hostPropertyForSkipable] = { ...bodyCopy[hostPropertyForSkipable], ...json.ui_handler.skipable_with }
    else bodyCopy = { ...bodyCopy, ...json.ui_handler.skipable_with }
  }

  if (response.status < 200 || response.status >= 300) throw json
  return json
}

export function httpGetQueryParams (params: any) {
  const flattenParams = httpJSONParamsFlatten(params)
  return (flattenParams.length ? '?' : '') + flattenParams.map(p => encodeURIComponent(p.key) + '=' + encodeURIComponent(p.value)).join('&')
}

export function httpGetFormData (params: any) {
  const formData = new FormData()
  for (const { key, value } of httpJSONParamsFlatten(params)) formData.append(key, value)
  return formData
}

/**
 * Transform a nested JSON to a flatten JSON suitable for FormBody multipart and query parameters.
 */
export function httpJSONParamsFlatten (params: any, opts = { keyPrefix: '' }) {
  const flattenParams: { key: string, value: any }[] = []

  for (const key in params) {
    if (typeof params[key] === 'undefined' || params[key] === null || params[key]?.length === 0) continue
    const formattedKey = opts.keyPrefix ? opts.keyPrefix + '[' + key + ']' : key

    if (key === 'q[id_not_in][]' || key === 'q[id_in][]') {
      // ApplicationController#uncompress_randack_array will uncompress this array. See related method for explanation.
      flattenParams.push({ key: key.slice(0, -2), value: params[key].join(',') })
    } else if (Array.isArray(params[key])) {
      for (let index = 0; index < params[key].length; index++) {
        const value = params[key][index]
        const isIndexed = typeof value === 'object' && !(value instanceof Blob)
        const formattedKeyArray = formattedKey.endsWith('[]') ? formattedKey : formattedKey + `[${isIndexed ? index : ''}]`
        if (isIndexed) flattenParams.push(...httpJSONParamsFlatten(value, { keyPrefix: formattedKeyArray }))
        else flattenParams.push({ key: formattedKeyArray, value })
      }
    } else if (typeof params[key] === 'object' && !(params[key] instanceof Blob)) {
      flattenParams.push(...httpJSONParamsFlatten(params[key], { keyPrefix: formattedKey }))
    } else {
      flattenParams.push({ key: formattedKey, value: params[key] })
    }
  }

  return flattenParams
}

export function httpGetCSRFToken () {
  // the csrf-token is not defined when running tests with capybara and rspec
  if (window.env === 'test' || window.noCsrf) return undefined

  const metas = document.getElementsByTagName('meta')
  for (let i = 0; i < metas.length; i++) {
    if (metas[i].getAttribute('name') === 'csrf-token') return metas[i].getAttribute('content')
  }

  throw new Error('Unable to find CSRF token')
}

/** This cache expires when the page changes */
export function httpWithInMemoryCache<Return, Fn extends (...args: any[]) => Promise<Return>> (httpEndpoint: Fn): Fn {
  const cacheItems: Record<string, Return> = {}

  return async function httpWithCacheWrapper (...args: any[]) {
    const cacheKey = JSON.stringify(args)
    if (cacheKey in cacheItems) return cacheItems[cacheKey]

    const result = await httpEndpoint(...args)
    cacheItems[cacheKey] = result
    return result
  } as Fn
}

type PersistentCacheKey = { appVersion: string, currentUserId: number, name: string, args: any[] }
type PersistentCacheEntry = { expiresAt: number, value: unknown }
const persistentCache: { cache: Record<string, PersistentCacheEntry> } = { cache: null }

export function httpWithPersistentCache<Return, Fn extends (...args: any[]) => Promise<Return>> (httpEndpoint: Fn, name: string, timeToLiveSeconds: number): Fn {
  const prefix = 'httpWithPersistentCache/'

  if (!persistentCache.cache) {
    // The persistent cache is shared between all components, so we load the data from the localStorage only once.
    persistentCache.cache = {}

    // By storing each cache item in a standalone localstorage entry, we can efficiently remove expired items or add new ones.
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)

      if (key.startsWith(prefix)) {
        const structuredKey = JSON.parse(key.substring(prefix.length)) as PersistentCacheKey

        if (structuredKey.appVersion !== window.oss_version || structuredKey.currentUserId !== window.currentUser?.id) {
          localStorage.removeItem(key)
          continue
        }
        
        const parsedEntry = JSON.parse(localStorage.getItem(key)) as PersistentCacheEntry

        if (parsedEntry.expiresAt < Date.now()) {
          localStorage.removeItem(key)
        } else {
          persistentCache.cache[key] = parsedEntry
        }
      }
    }
  }
  
  return async function httpWithCacheWrapper (...args: unknown[]) {
    const structuredKey = {
      appVersion: window.oss_version,
      currentUserId: window.currentUser?.id,
      name,
      args
    }

    const cacheKey = prefix + JSON.stringify(structuredKey)
    if (cacheKey in persistentCache.cache && persistentCache.cache[cacheKey].expiresAt >= Date.now()) return persistentCache.cache[cacheKey].value

    const result = await httpEndpoint(...args)
    const cacheItem = { expiresAt: Date.now() + timeToLiveSeconds * 1000, value: result }
    persistentCache.cache[cacheKey] = cacheItem
    localStorage.setItem(cacheKey, JSON.stringify(cacheItem))
    return result
  } as Fn
}

export function httpParamsRemoveDiacriticsToSortableNames<T extends object> (query: T): T {
  const newQuery = { ...query } as Record<string, string>

  for (const key in newQuery) {
    if (key.includes('sortable_name') && newQuery[key]) {
      newQuery[key] = removeDiacritics(newQuery[key].toLocaleLowerCase())
    }
  }

  return newQuery as T
}

function httpProcessResponse (response: Response) {
  // Check for maintenance mode
  if (response.status === 503) {
    window.location.href = '/'

    // while the redirection is processing, displaythe maintenance message
    return httpParseJsonAndFlash(response)
  }

  // response.status not in the range 200-299
  if (!response.ok) {
    // Clone response to avoid "Body has already been consumed" error when re-reading response body
    // if error uncaught by httpParseJsonAndFlash
    return httpParseJsonAndFlash(response.clone())
      .catch(async () => {
        // We only import Rollbar here due to casting_estimative_price_service_spec.rb (otherwise ReferenceError: window is not defined)
        if (typeof window !== 'undefined') {
          const { default: Rollbar } = await import('../../../utils/rollbar')
          Rollbar.error(
            new Error(`HTTP ERROR ${response.status} - ${response.url} (${response.statusText})`),
            { content: await response.text() }
          )
        }

        throw response
      })
      .then(err => { throw err })
  }

  const parsedUrl = new URL(response.url)
  return parsedUrl.pathname.endsWith('.xml')
    ? response.text()
    : parsedUrl.pathname.endsWith('.pdf') || parsedUrl.pathname.endsWith('.jpg')
    ? response.blob()
    : httpParseJsonAndFlash(response)
}

export function httpParseJsonAndFlash (response: Response) {
  // Parse location header for devise controllers, that use them instead of redirect_to
  if (response.status === 204 /* No Content */) {
    return response.text().then(() => {
      const json: any = {}
      if (response.headers.has('location')) json.redirect_url = response.headers.get('location')
      return json
    })
  }

  return response.json().then(json => {
    if (json.flash) {
      for (const type in json.flash) {
        flash$.next({ content: json.flash[type], type: type as any })
      }
    }

    // Parse location header for devise controllers, that use them instead of redirect_to
    if (!('redirect_url' in Object(json)) && response.headers.has('location')) {
      json.redirect_url = response.headers.get('location')
    }

    return json
  })
}

// expose http service for easier testing in dev mode
if (typeof window !== 'undefined') {
  Object.assign(window, { httpGet, httpPost, httpPatch, httpPut, httpDelete })
}
